<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
        @click.self="closeModal"
      >
        <div
          class="bg-white rounded-2xl shadow-[8px_8px_0_0_rgba(0,0,0,1)] border-4 border-black max-w-lg w-full p-6 transform transition-all"
        >
          <!-- Header -->
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold text-gray-900 flex items-center">
              <ArrowDownTrayIcon class="w-7 h-7 mr-3 text-blue-600" />
              Choose Export Type
            </h2>
            <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close modal"
            >
              <XMarkIcon class="w-6 h-6" />
            </button>
          </div>

          <!-- Content -->
          <p class="text-gray-600 mb-6">Select what you'd like to download:</p>

          <!-- Export Options -->
          <div class="space-y-4">
            <!-- Original Notes Option -->
            <button
              v-if="hasOriginalNotes"
              @click="exportOriginalNotes"
              class="w-full group relative overflow-hidden px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white border-2 border-purple-700 shadow-[4px_4px_0_0_rgba(88,28,135,1)] rounded-xl hover:shadow-[6px_6px_0_0_rgba(88,28,135,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200 font-medium text-left"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <DocumentTextIcon class="w-6 h-6 mr-3 flex-shrink-0" />
                  <div>
                    <div class="font-bold text-lg">Original Student Notes</div>
                    <div class="text-purple-100 text-sm mt-1">
                      Download the file or notes you uploaded
                      <span v-if="material?.filename" class="font-medium"
                        >({{ material.filename }})</span
                      >
                    </div>
                  </div>
                </div>
                <ArrowDownTrayIcon class="w-5 h-5 ml-3 flex-shrink-0 group-hover:animate-bounce" />
              </div>
            </button>

            <!-- No Original Notes Message -->
            <div
              v-else
              class="w-full px-6 py-4 bg-gray-100 text-gray-500 border-2 border-gray-300 rounded-xl font-medium text-left opacity-50 cursor-not-allowed"
            >
              <div class="flex items-center">
                <DocumentTextIcon class="w-6 h-6 mr-3 flex-shrink-0" />
                <div>
                  <div class="font-bold text-lg">Original Student Notes</div>
                  <div class="text-gray-400 text-sm mt-1">
                    No original notes available for this material
                  </div>
                </div>
              </div>
            </div>

            <!-- AI-Generated Content Option -->
            <button
              @click="exportAIGenerated"
              class="w-full group relative overflow-hidden px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white border-2 border-green-700 shadow-[4px_4px_0_0_rgba(21,128,61,1)] rounded-xl hover:shadow-[6px_6px_0_0_rgba(21,128,61,1)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200 font-medium text-left"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <SparklesIcon class="w-6 h-6 mr-3 flex-shrink-0" />
                  <div>
                    <div class="font-bold text-lg">AI-Generated Content</div>
                    <div class="text-green-100 text-sm mt-1">
                      Download the {{ material?.type.toLowerCase() }} created by AI
                    </div>
                  </div>
                </div>
                <ArrowDownTrayIcon class="w-5 h-5 ml-3 flex-shrink-0 group-hover:animate-bounce" />
              </div>
            </button>
          </div>

          <!-- Cancel Button -->
          <button
            @click="closeModal"
            class="w-full mt-6 px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 shadow-[2px_2px_0_0_rgba(0,0,0,1)] rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  ArrowDownTrayIcon,
  XMarkIcon,
  DocumentTextIcon,
  SparklesIcon,
} from "@heroicons/vue/24/outline";
import { logDebug, logInfo } from "@/utils/secureLogger";

interface AIStudyMaterial {
  id: string;
  title: string;
  type: "Summary" | "Flashcards" | "Quiz";
  subject: string;
  source: string;
  filename?: string;
  date: Date | string;
  description: string;
  keyPoints: string;
  keyPointsList?: string[];
  student_notes_url?: string;
}

interface Props {
  isOpen: boolean;
  material: AIStudyMaterial | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  exportOriginal: [material: AIStudyMaterial];
  exportAI: [material: AIStudyMaterial];
}>();

const hasOriginalNotes = computed(() => {
  return !!props.material?.student_notes_url;
});

const closeModal = () => {
  logDebug("Export modal closed");
  emit("close");
};

const exportOriginalNotes = () => {
  if (props.material) {
    logInfo("User selected to export original notes", {
      materialId: props.material.id,
      filename: props.material.filename,
    });
    emit("exportOriginal", props.material);
    closeModal();
  }
};

const exportAIGenerated = () => {
  if (props.material) {
    logInfo("User selected to export AI-generated content", {
      materialId: props.material.id,
      type: props.material.type,
    });
    emit("exportAI", props.material);
    closeModal();
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.9);
  opacity: 0;
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.group:hover .group-hover\:animate-bounce {
  animation: bounce 0.6s ease-in-out infinite;
}
</style>
